import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AlertCircle, Info } from "lucide-react";
import { IphonePreview } from "@/components/marketing/IphonePreview";
import { useMarketingSettings } from "@/hooks/sms/useMarketingSettings";
import { useInviteRotation, useUpdateInviteRotation } from "@/hooks/sms/useInviteRotation";
import { useJoinKeyword, useUpdateJoinKeyword } from "@/hooks/sms/useJoinKeyword";
import { useDisclosures } from "@/hooks/sms/useDisclosures";
import { validateInviteText, validateWelcomeText, type ValidationIssue } from "@/utils/inviteValidation";
import { renderInviteBody, inviteSegments } from "@/utils/inviteForecast";
import { renderTemplate, DEFAULT_INVITE_MESSAGE_BODY } from "@/utils/smsShared";
import { formatCurrency } from "@/utils/orderUtils";

/** IR-5 issues, errors first, in owner words. */
function IssueList({ issues }: { issues: ValidationIssue[] }) {
  if (issues.length === 0) {
    return (
      <p className="flex items-center gap-1.5 text-xs text-status-success">
        <Info className="h-3.5 w-3.5" aria-hidden="true" /> Meets the compliance rules.
      </p>
    );
  }
  return (
    <ul className="space-y-1">
      {issues.map((issue) => (
        <li
          key={issue.code}
          className={`flex items-start gap-1.5 text-xs ${
            issue.severity === "error" ? "text-status-danger" : "text-status-warning"
          }`}
        >
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>{issue.message}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * The invite and welcome text editors (PR 1.3), with IR-5 validation and a
 * phone preview of the invite exactly as it lands. The invite writes
 * sms_invite_rotations.invite_message_body (NULL = the built-in default); the
 * welcome writes the JOIN keyword's response_body. Save is blocked while an
 * IR-5 "error" stands; warnings (e.g. no {offer_link} yet) still allow saving.
 */
export function InviteTextEditors() {
  const { data: settings } = useMarketingSettings();
  const { data: rotation } = useInviteRotation();
  const { data: joinKeyword } = useJoinKeyword();
  const { data: disclosures } = useDisclosures();
  const updateRotation = useUpdateInviteRotation();
  const updateJoin = useUpdateJoinKeyword();

  const [inviteBody, setInviteBody] = useState("");
  const [welcomeBody, setWelcomeBody] = useState("");

  const storedInvite = rotation ? (rotation.invite_message_body ?? DEFAULT_INVITE_MESSAGE_BODY) : null;
  const storedWelcome = joinKeyword ? joinKeyword.response_body : null;

  useEffect(() => {
    if (storedInvite !== null) setInviteBody(storedInvite);
  }, [storedInvite]);
  useEffect(() => {
    if (storedWelcome !== null) setWelcomeBody(storedWelcome);
  }, [storedWelcome]);

  if (!settings) return null;

  const vars = {
    storeName: settings.store_name,
    storePhone: settings.store_phone,
    storeAddress: settings.store_address,
  };
  const welcomeFooter = disclosures?.welcome_footer?.body ?? "";
  const sampleLink = `${settings.public_base_url}/o/XXXXXXXXXX`;

  const inviteValidation = validateInviteText(inviteBody, settings.store_name);
  const segments = inviteSegments(inviteBody, vars);
  const costPerInvite = segments * settings.sms_cost_per_segment;
  const renderedInvite = renderInviteBody(inviteBody, vars);

  const welcomeValidation = validateWelcomeText(welcomeBody, welcomeFooter);
  const renderedWelcome = renderTemplate(welcomeBody, {
    location_name: settings.store_name,
    compliance_footer: welcomeFooter,
    offer_link: sampleLink,
    store_phone: settings.store_phone ?? "",
    store_address: settings.store_address,
  });

  const inviteDirty = storedInvite !== null && inviteBody !== storedInvite;
  const welcomeDirty = storedWelcome !== null && welcomeBody !== storedWelcome;

  const saveInvite = () => {
    if (!rotation || !inviteValidation.ok) return;
    updateRotation.mutate({ store_id: rotation.store_id, invite_message_body: inviteBody });
  };
  const saveWelcome = () => {
    if (!joinKeyword || !welcomeValidation.ok) return;
    updateJoin.mutate({ id: joinKeyword.id, response_body: welcomeBody });
  };

  return (
    <Card>
      <CardContent className="space-y-6 p-4 sm:p-6">
        <div>
          <h3 className="text-base font-semibold text-foreground">Message text</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            The invite customers receive, and the welcome they get when they reply Y. Use {"{location_name}"} for your
            store name and {"{compliance_footer}"} for the required STOP/terms line.
          </p>
        </div>

        {/* Invite editor + phone preview */}
        <div className="grid gap-5 lg:grid-cols-[1fr_auto]">
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="invite-body">Invite text</Label>
              <span className="text-xs text-muted-foreground">
                {segments} segment{segments === 1 ? "" : "s"} · {formatCurrency(costPerInvite)}/invite
              </span>
            </div>
            <Textarea
              id="invite-body"
              className="min-h-[110px] text-base"
              value={inviteBody}
              onChange={(e) => setInviteBody(e.target.value)}
            />
            <IssueList issues={inviteValidation.issues} />
            <Button
              type="button"
              className="h-11 sm:h-10"
              disabled={!inviteValidation.ok || !inviteDirty || updateRotation.isPending}
              onClick={saveInvite}
            >
              Save invite text
            </Button>
          </div>
          <div className="lg:w-[280px]">
            <IphonePreview body={renderedInvite} storeName={settings.store_name} storePhone={settings.store_phone} />
          </div>
        </div>

        {/* Welcome editor */}
        <div className="space-y-2 border-t border-border pt-6">
          <Label htmlFor="welcome-body">Welcome text (after they reply Y)</Label>
          <Textarea
            id="welcome-body"
            className="min-h-[90px] text-base"
            value={welcomeBody}
            onChange={(e) => setWelcomeBody(e.target.value)}
          />
          <IssueList issues={welcomeValidation.issues} />
          {renderedWelcome && (
            <p className="rounded-lg border border-border bg-muted/40 p-3 text-xs leading-relaxed text-muted-foreground">
              <span className="mb-1 block font-medium text-foreground">As it lands:</span>
              <span className="whitespace-pre-wrap break-words">{renderedWelcome}</span>
            </p>
          )}
          <Button
            type="button"
            className="h-11 sm:h-10"
            disabled={!welcomeValidation.ok || !welcomeDirty || updateJoin.isPending || !joinKeyword}
            onClick={saveWelcome}
          >
            Save welcome text
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
